import { Vector3d } from "./vector3d";
import { AU_KM } from "./constants";
import type { StateVector } from "./state_vector";

/**
 * Coordinate frame transformations for orbital mechanics.
 *
 * The perifocal frame has its x-axis pointing toward periapsis,
 * y-axis in the direction of motion at periapsis, and z-axis
 * normal to the orbital plane. Three Euler rotations convert
 * this to the ecliptic (inertial) frame.
 */

/**
 * Rotate a vector from the perifocal (orbital plane) frame to the ecliptic frame.
 *
 * Applies the rotation R_z(LAN) * R_x(inc) * R_z(AoP) which maps
 * the orbital plane into the ecliptic reference frame.
 *
 * @param v   Vector in perifocal frame
 * @param lan Longitude of ascending node (radians)
 * @param inc Inclination (radians)
 * @param aop Argument of periapsis (radians)
 * @returns   Vector in ecliptic frame
 */
export function perifocalToEcliptic(
  v: Vector3d,
  lan: number,
  inc: number,
  aop: number,
): Vector3d {
  const cosO = Math.cos(lan);
  const sinO = Math.sin(lan);
  const cosI = Math.cos(inc);
  const sinI = Math.sin(inc);
  const cosW = Math.cos(aop);
  const sinW = Math.sin(aop);

  // Rotation matrix elements (only the 6 needed since v.z = 0 in perifocal)
  const r11 = cosO * cosW - sinO * sinW * cosI;
  const r12 = -cosO * sinW - sinO * cosW * cosI;
  const r21 = sinO * cosW + cosO * sinW * cosI;
  const r22 = -sinO * sinW + cosO * cosW * cosI;
  const r31 = sinW * sinI;
  const r32 = cosW * sinI;

  return new Vector3d(
    r11 * v.x + r12 * v.y,
    r21 * v.x + r22 * v.y,
    r31 * v.x + r32 * v.y,
  );
}

/**
 * Rotate a vector from the ecliptic frame to the perifocal (orbital plane) frame.
 * Inverse of perifocalToEcliptic (transpose of the orthogonal rotation matrix).
 *
 * @param v   Vector in ecliptic frame
 * @param lan Longitude of ascending node (radians)
 * @param inc Inclination (radians)
 * @param aop Argument of periapsis (radians)
 * @returns   Vector in perifocal frame
 */
export function eclipticToPerifocal(
  v: Vector3d,
  lan: number,
  inc: number,
  aop: number,
): Vector3d {
  const cosO = Math.cos(lan);
  const sinO = Math.sin(lan);
  const cosI = Math.cos(inc);
  const sinI = Math.sin(inc);
  const cosW = Math.cos(aop);
  const sinW = Math.sin(aop);

  // Transposed rotation matrix
  const r11 = cosO * cosW - sinO * sinW * cosI;
  const r12 = sinO * cosW + cosO * sinW * cosI;
  const r13 = sinW * sinI;
  const r21 = -cosO * sinW - sinO * cosW * cosI;
  const r22 = -sinO * sinW + cosO * cosW * cosI;
  const r23 = cosW * sinI;

  return new Vector3d(
    r11 * v.x + r12 * v.y + r13 * v.z,
    r21 * v.x + r22 * v.y + r23 * v.z,
    0, // z component is ~0 for in-plane vectors
  );
}

/** Convert heliocentric position to body-centered position. */
export function heliocentricToBodyCentered(
  helioPos: Vector3d,
  bodyHelioPos: Vector3d,
): Vector3d {
  return helioPos.subtract(bodyHelioPos);
}

/** Convert body-centered position to heliocentric position. */
export function bodyCenteredToHeliocentric(
  bodyRelPos: Vector3d,
  bodyHelioPos: Vector3d,
): Vector3d {
  return bodyRelPos.add(bodyHelioPos);
}

/** Convert heliocentric state vector to body-centered state vector. */
export function heliocentricStateToBodyCentered(
  helioState: StateVector,
  bodyHelioState: StateVector,
  bodyName: string,
): StateVector {
  return {
    position: helioState.position.subtract(bodyHelioState.position),
    velocity: helioState.velocity.subtract(bodyHelioState.velocity),
    time: helioState.time,
    centralBody: bodyName,
  };
}

/** Convert body-centered state vector to heliocentric state vector. */
export function bodyCenteredStateToHeliocentric(
  bodyRelState: StateVector,
  bodyHelioState: StateVector,
): StateVector {
  return {
    position: bodyRelState.position.add(bodyHelioState.position),
    velocity: bodyRelState.velocity.add(bodyHelioState.velocity),
    time: bodyRelState.time,
    centralBody: "Sun",
  };
}

/** Convert position in AU to position in km. */
export function auToKm(posAU: Vector3d): Vector3d {
  return posAU.scale(AU_KM);
}

/** Convert position in km to position in AU. */
export function kmToAu(posKm: Vector3d): Vector3d {
  return posKm.divideBy(AU_KM);
}
